"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { MouseEvent, TransitionEvent } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { getStrapiMedia } from "@/lib/strapi/client";
import { CmsCta } from "@/components/ui";
import { isNavActive, navLinkToHref } from "@/lib/navigation";
import {
  handleInPageSectionClick,
  isInPageSectionHref,
  scrollToSectionId,
} from "@/lib/section-anchors";
import { getCtaConfig } from "@/lib/cta";
import type {
  HeaderType,
  SpecialPopupType,
  TopBannerType,
} from "@/lib/types/global-content";
import type { CtaType } from "@/lib/types/shared";
import {
  isHyTourNavLink,
  isScheduleTourNavItem,
  withHyTourClassWhen,
  withHyTourLinkClass,
} from "@/lib/hyTour";
import SpecialPopup from "./SpecialPopup";
import TopBanner from "./TopBanner";
import { OPENS_IN_NEW_TAB_HINT } from "@/lib/a11y";

const POPUP_DISMISSED_KEY = "special-popup-dismissed";

const subscribeNoop = () => () => {};

function useIsClient() {
  return useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );
}

function useSessionFlag(key: string) {
  return useSyncExternalStore(
    subscribeNoop,
    () => sessionStorage.getItem(key) === "1",
    () => false,
  );
}

function isHomePath(pathname: string | null): boolean {
  return pathname === "/" || pathname === "/home";
}

function logoHref(url: string | null | undefined): string {
  if (!url || url === "/" || url === "home") return "/";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("/")
  )
    return url;
  return "/";
}

/** Header CTA sizes from design; colors follow API `buttonStyle` + header layout theme. */
function headerCtaClass(
  cta: CtaType,
  compact: boolean,
  layout: string,
): string {
  const style = getCtaConfig(cta).buttonStyle;
  const themeB = layout.trim() === "Layout B";
  // Desktop: 10px 20px. Mobile: 8px 17px. Both use width auto plus the design min-width.
  const size = compact
    ? themeB
      ? "touch-target h-9 w-auto min-w-[96px] py-2 px-[17px] text-[12px] leading-5 tracking-[-0.5px]"
      : "touch-target h-8 w-auto min-w-[96px] py-2 px-[17px] text-[12px] leading-5"
    : themeB
      ? "h-10 w-auto min-w-[136px] py-[10px] px-5 text-[14px] leading-5 tracking-[-0.5px]"
      : "h-10 w-auto min-w-[114px] py-[10px] px-5 text-[14px] leading-5 tracking-[-0.5px]";
  const radius = themeB ? "rounded-[2px]" : "rounded-pill";
  const base = `inline-flex items-center justify-center box-border ${radius} font-body font-bold whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 ${
    themeB ? "focus-visible:ring-forest" : "focus-visible:ring-brand-primary"
  } ${size}`;

  if (style === "Secondary") {
    return `${base} header-cta header-cta--secondary border-[1.5px] border-cta-secondary bg-cta-secondary-bg text-cta-secondary hover:border-cta-secondary-hover-border hover:bg-cta-secondary-hover hover:text-cta-secondary-hover-text active:opacity-90`;
  }

  // Primary (e.g. Learn more) — defaults #232F19 / white; hover → Primary fill.
  // Children restyle via cssConfigurations targeting `.header-cta` / `[data-fig="header-cta"]`.
  return `${base} header-cta header-cta--primary border-[1.5px] border-transparent bg-header-cta-bg text-header-cta-text hover:border-cta-primary-border hover:bg-cta-primary hover:text-cta-primary-text active:opacity-90`;
}

function HeaderRightNavCta({
  cta,
  compact = false,
  layout = "Layout A",
}: {
  cta: CtaType;
  compact?: boolean;
  layout?: string;
}) {
  return (
    <CmsCta
      cta={cta}
      unstyled
      showLinkArrow={false}
      data-fig="header-cta"
      className={headerCtaClass(cta, compact, layout)}
    />
  );
}

type Props = {
  header: HeaderType | null;
  topBanner: TopBannerType | null;
  specialPopup: SpecialPopupType | null;
  scheduleTourUrl?: string | null;
};

export default function SiteHeader({
  header,
  topBanner,
  specialPopup,
  scheduleTourUrl = null,
}: Props) {
  const pathname = usePathname();
  const isClient = useIsClient();
  const popupDismissedStored = useSessionFlag(POPUP_DISMISSED_KEY);
  // Banner dismiss is in-memory only — refresh should show the top bar again.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [popupReady, setPopupReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuRendered, setMobileMenuRendered] = useState(false);

  const openMobileMenu = useCallback(() => {
    setMobileMenuRendered(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setMobileMenuOpen(true));
    });
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleMobileMenuTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== "transform") return;
      if (event.target !== event.currentTarget) return;
      if (!mobileMenuOpen) setMobileMenuRendered(false);
    },
    [mobileMenuOpen],
  );

  const bannerVisible = !bannerDismissed;
  const hasPopupContent = Boolean(
    specialPopup?.title || specialPopup?.description,
  );
  const popupOpen =
    isClient &&
    popupReady &&
    isHomePath(pathname) &&
    hasPopupContent &&
    !popupDismissedStored &&
    !popupDismissed;

  useEffect(() => {
    let timeoutId: number | undefined;
    const show = () => {
      timeoutId = window.setTimeout(() => setPopupReady(true), 1800);
    };
    if (document.readyState === "complete") {
      show();
    } else {
      window.addEventListener("load", show, { once: true });
    }
    return () => {
      window.removeEventListener("load", show);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const main = document.getElementById("main-content");
    const footer = document.querySelector("footer");
    if (popupOpen || mobileMenuOpen) {
      main?.setAttribute("inert", "");
      footer?.setAttribute("inert", "");
    } else {
      main?.removeAttribute("inert");
      footer?.removeAttribute("inert");
    }
    return () => {
      main?.removeAttribute("inert");
      footer?.removeAttribute("inert");
    };
  }, [popupOpen, mobileMenuOpen]);

  useEffect(() => {
    closeMobileMenu();
  }, [pathname, closeMobileMenu]);

  useEffect(() => {
    if (!mobileMenuRendered) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMobileMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuRendered, closeMobileMenu]);

  // Scroll to `#section` on load / when the hash changes (home page).
  useEffect(() => {
    if (!isHomePath(pathname)) return;

    const scrollToHash = (behavior: ScrollBehavior) => {
      const id = window.location.hash.replace(/^#/, "").trim();
      if (!id) return;
      requestAnimationFrame(() => {
        scrollToSectionId(id, behavior);
      });
    };

    scrollToHash("smooth");

    const onHashChange = () => scrollToHash("smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [pathname]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
  }, []);

  const closePopup = useCallback(() => {
    sessionStorage.setItem(POPUP_DISMISSED_KEY, "1");
    setPopupDismissed(true);
  }, []);

  const handleSectionNavClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!isHomePath(pathname)) {
        // Let Next navigate to `/#id`; home effect will scroll.
        return;
      }
      handleInPageSectionClick(event, href);
    },
    [pathname],
  );

  if (!header) return null;

  const logo = header.headerLogo;
  const mainNav = header.mainNavigationMenu ?? [];
  const rightNav = header.rightNavigationMenu ?? [];
  const mobileMenu = (header.mobileHamburgerMenu ?? []).filter((item) =>
    Boolean(item.label?.trim()),
  );
  const showHamburger = mobileMenu.length > 0;
  const layout = header.headerLayout?.trim() || "Layout A";
  const isLayoutB = layout === "Layout B";
  const showBanner = bannerVisible && !!topBanner?.title;
  const headerSpacerHeight = showBanner
    ? "calc(var(--height-specials-bar) + var(--height-navbar))"
    : "var(--height-navbar)";
  const headerBg = header.backgroundColor?.trim() || "#F6F0E3";
  const specialsBarActive = showBanner ? "var(--height-specials-bar)" : "0px";

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `:root{--height-specials-bar-active:${specialsBarActive};--height-header:${headerSpacerHeight};}`,
        }}
      />
      <header
        className="fixed top-0 left-0 right-0 z-50 flex w-full flex-col pr-[var(--removed-body-scroll-bar-size,0px)]"
        aria-label="Site header"
        data-header-layout={layout}
      >
        {showBanner && (
          <TopBanner
            data={topBanner}
            onDismiss={dismissBanner}
            onSectionLinkClick={handleSectionNavClick}
            scheduleTourUrl={scheduleTourUrl}
          />
        )}

        <div
          className={`box-border flex h-[var(--height-navbar)] w-full shrink-0 items-center border-b backdrop-blur-[6px] ${
            isLayoutB
              ? "border-[rgba(28,28,26,0.1)] md:border-border"
              : "border-[rgba(28,28,26,0.1)] md:border-[#CED8C6]"
          }`}
          style={{ backgroundColor: headerBg }}
        >
          <div className="w-full px-4 md:px-spacing-section-x">
            <div
              className={`mx-auto flex w-full max-w-width-content items-center justify-between gap-4 ${
                isLayoutB ? "h-9 md:h-10 md:gap-8" : "h-10 md:gap-8"
              }`}
            >
              {logo && (
                <Link
                  href={logoHref(logo.url)}
                  className="flex min-w-0 shrink-0 items-center"
                  aria-label={logo.label ?? "Home"}
                >
                  {logo.icon ? (
                    <Image
                      src={getStrapiMedia(logo.icon.url) ?? ""}
                      alt=""
                      width={114}
                      height={40}
                      className={`w-auto max-w-[min(160px,50vw)] object-contain object-left ${
                        isLayoutB ? "h-9 md:h-10" : "h-10"
                      }`}
                      loading="eager"
                    />
                  ) : logo.label ? (
                    <span
                      className={`truncate font-heading font-bold text-forest ${
                        isLayoutB
                          ? "text-[24px] leading-9 md:text-[27px]"
                          : "text-[27px] leading-9"
                      }`}
                    >
                      {logo.label}
                    </span>
                  ) : null}
                </Link>
              )}

              {/* Desktop center nav */}
              {mainNav.length > 0 && (
                <nav
                  className="hidden min-w-0 flex-1 flex-nowrap items-center justify-center gap-4 lg:flex xl:gap-8"
                  aria-label="Primary"
                >
                  {mainNav.map((item) => {
                    const href = navLinkToHref(item, { scheduleTourUrl });
                    const isTour = isHyTourNavLink(item, scheduleTourUrl);
                    const isTourItem = isScheduleTourNavItem(item);
                    const isExternal =
                      !isTour && item.linkType === "ExternalLink";
                    const isSection = !isTour && isInPageSectionHref(href);
                    const active =
                      !isExternal &&
                      !isSection &&
                      !isTour &&
                      !isTourItem &&
                      isNavActive(pathname, href);
                    const openNewTab =
                      Boolean(item.newTab) && !isSection && !isTour;
                    // Multifamily: TourUrl → `.hytour-link` (HyPopup click target).
                    const navClassName = withHyTourLinkClass(
                      withHyTourClassWhen(
                        `shrink-0 whitespace-nowrap font-body text-[14px] leading-5 transition-opacity hover:opacity-80 ${
                          isLayoutB
                            ? active
                              ? "font-medium text-forest"
                              : "font-normal text-stone"
                            : active
                              ? "font-medium text-[#232F19]"
                              : "font-normal text-[#575757]"
                        }`,
                        isTour,
                      ),
                      item.linkType,
                    )
                    // Plain <a> for Hy.ly — Next <Link> intercepts the click.
                    if (isTour) {
                      return (
                        <a key={item.id} href="#" className={navClassName}>
                          {item.label}
                        </a>
                      )
                    }
                    return (
                      <Link
                        key={item.id}
                        href={href}
                        onClick={(event) =>
                          handleSectionNavClick(event, href)
                        }
                        className={navClassName}
                        {...(openNewTab
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {item.label}
                        {openNewTab ? (
                          <span className="sr-only">
                            {OPENS_IN_NEW_TAB_HINT}
                          </span>
                        ) : null}
                      </Link>
                    );
                  })}
                </nav>
              )}

              {(rightNav.length > 0 || showHamburger) && (
                <div
                  className={`ml-auto flex shrink-0 items-center gap-2 ${
                    rightNav.length > 0 ? "md:ml-0" : ""
                  }`}
                >
                  {rightNav.length > 0 && (
                    <>
                      <div className="hidden items-center gap-3 md:flex">
                        {rightNav.map((link) => (
                          <HeaderRightNavCta
                            key={link.id}
                            cta={link}
                            layout={layout}
                          />
                        ))}
                      </div>
                      <div className="flex items-center md:hidden">
                        {rightNav.slice(0, 1).map((link) => (
                          <HeaderRightNavCta
                            key={link.id}
                            cta={link}
                            compact
                            layout={layout}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {showHamburger && (
                    <button
                      type="button"
                      className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md text-icon-fill md:hidden"
                      aria-label="Open navigation menu"
                      aria-expanded={mobileMenuOpen}
                      aria-controls="mobile-hamburger-menu"
                      onClick={openMobileMenu}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 5H17M3 10H17M3 15H17"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Full-view mobile menu — slides down from top (open) / up (close). */}
      {showHamburger && mobileMenuRendered && (
        <div
          id="mobile-hamburger-menu"
          className={`fixed inset-0 z-[60] flex flex-col bg-brand-primary transition-transform duration-300 ease-out md:hidden ${
            mobileMenuOpen ? "translate-y-0" : "-translate-y-full"
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
          onTransitionEnd={handleMobileMenuTransitionEnd}
        >
          <button
            type="button"
            className="absolute right-6 top-6 z-10 inline-flex h-[39px] w-[39px] cursor-pointer items-center justify-center p-2 text-white"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          >
            <svg
              width="23"
              height="23"
              viewBox="0 0 23 23"
              fill="none"
              aria-hidden="true"
            >
              <line
                x1="1"
                y1="1"
                x2="22"
                y2="22"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="22"
                y1="1"
                x2="1"
                y2="22"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <nav
            aria-label="Main navigation"
            className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto px-5 py-20"
          >
            <ul
              className="mx-auto flex w-full flex-col items-center gap-[50px]"
              role="list"
            >
              {mobileMenu.map((item) => {
                const href = navLinkToHref(item, { scheduleTourUrl });
                const isTour = isHyTourNavLink(item, scheduleTourUrl);
                const isTourItem = isScheduleTourNavItem(item);
                const isExternal =
                  !isTour && item.linkType === "ExternalLink";
                const isSection = !isTour && isInPageSectionHref(href);
                const active =
                  !isExternal &&
                  !isSection &&
                  !isTour &&
                  !isTourItem &&
                  isNavActive(pathname, href);
                const openNewTab =
                  Boolean(item.newTab) && !isSection && !isTour;
                const mobileClassName = withHyTourLinkClass(
                  withHyTourClassWhen(
                    `block text-center font-body text-[20px] font-normal uppercase leading-[30px] tracking-[-0.4px] text-white transition-opacity hover:opacity-80 ${
                      active ? "underline underline-offset-4" : ""
                    }`,
                    isTour,
                  ),
                  item.linkType,
                )
                return (
                  <li key={item.id}>
                    {isTour ? (
                      <a
                        href="#"
                        className={mobileClassName}
                        onClick={() => closeMobileMenu()}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        href={href}
                        aria-current={active ? "page" : undefined}
                        onClick={(event) => {
                          handleSectionNavClick(event, href);
                          if (!openNewTab) closeMobileMenu();
                        }}
                        className={mobileClassName}
                        {...(openNewTab
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {item.label}
                        {openNewTab ? (
                          <span className="sr-only">{OPENS_IN_NEW_TAB_HINT}</span>
                        ) : null}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      )}

      <div
        className="w-full shrink-0"
        style={{ height: headerSpacerHeight }}
        aria-hidden="true"
      />

      <SpecialPopup data={specialPopup} open={popupOpen} onClose={closePopup} />
    </>
  );
}
